import { useRef, useEffect, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { DynamicVariableChannelData } from 'types';
// Import Enums
import { TemplateValueTypeEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import Helpers
import { replaceDomainFromEnvironmentURL } from 'services/Helpers';
import { createMosaicoConfigsViaHTMLString } from './helper';
// Import Services
import { generatePartialHtml, htmlStringToDomElement } from 'services/template';
import { stripExecutableHtml, stripExecutableHtmlDeep } from 'services/html';
// Import UI Components
import { SkeletonLoader } from '@geneui/components';
// Import Styles
import styles from './mosaicoEditor.module.scss';

const dynamicVariableIcons = {
    [TemplateValueTypeEnum.Link]: 'link',
    [TemplateValueTypeEnum.Image]: 'image',
};

interface OnChangeParamConfig {
    html: string;
    content: string;
    metadata: string;
}

interface DynamicValueItem {
    name: string;
    value: string;
    preview?: string;
    isURL?: boolean;
    icon?: string;
    isDisabled?: boolean;
    disableDescription?: string;
}

interface MosaicoEditorRef extends Omit<HTMLIFrameElement, 'contentWindow'> {
    contentWindow: {
        $react: {
            onChange: (config: OnChangeParamConfig) => void;
            onFocus?: () => void;
            dynamicValues: DynamicValueItem[];
            disableDescription: string;
        };
        contentProviderUrl: string;
        initMosaico: (config: Nilable<AnyObject>, baseUrl: string) => void;
    };
}

interface Props {
    isPartialHTML?: boolean;
    config?: AnyObject;
    defaultHTML?: string;
    dynamicVariables: DynamicVariableChannelData[];
    dynamicVariableChannelKey: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    onConfigChange: (config: { content: string; metadata: AnyObject }) => void;
}

const MosaicoEditor: React.FC<Props> = (props) => {
    const {
        isPartialHTML,
        config,
        defaultHTML,
        dynamicVariables,
        dynamicVariableChannelKey,
        onChange,
        onFocus,
        onConfigChange,
    } = props;
    const { t } = useTranslation();
    const { isLoadingPartnerSettings } = useHasPermission();

    const editorRef = useRef<MosaicoEditorRef>(null);
    const baseUrl = replaceDomainFromEnvironmentURL(process.env.REACT_APP_CRM_GALLERY_URL);
    const parsedDynamicVariables = useMemo(
        () =>
            (dynamicVariables || []).map((variable: DynamicVariableChannelData): DynamicValueItem => {
                const { FieldName, TypeConfig, isDisabled, isBlocked, PreviewValue } =
                    variable.channels[dynamicVariableChannelKey] || {};

                return {
                    name: t(variable.displayNameKey),
                    value: FieldName,
                    isURL: TypeConfig?.ValueType === TemplateValueTypeEnum.Link,
                    icon: dynamicVariableIcons[TypeConfig?.ValueType || ''],
                    preview: PreviewValue,
                    ...(isDisabled || isBlocked
                        ? {
                              isDisabled: true,
                              disableDescription: isBlocked ? t(l.TheFeatureIsNotIncludedInYourPackage) : undefined,
                          }
                        : {}),
                };
            }),
        [dynamicVariables, t, dynamicVariableChannelKey],
    );

    const handleChange = ({ html, content, metadata }: OnChangeParamConfig) => {
        const htmlDocument = htmlStringToDomElement(html);
        const isEmpty = htmlDocument.body.children[0]?.children.length === 0;
        const value = isEmpty ? '' : isPartialHTML ? generatePartialHtml(htmlDocument) : html;

        onChange(value);
        onConfigChange({ content, metadata: JSON.parse(metadata) });
    };

    const initMosaico = () => {
        if (!editorRef.current) return;

        if (editorRef.current.contentWindow) {
            editorRef.current.contentWindow.$react = {
                onChange: handleChange,
                onFocus: onFocus,
                dynamicValues: parsedDynamicVariables,
                disableDescription: t(l.TheFeatureIsNotIncludedInYourPackage),
            };
        }

        editorRef.current.onload = () => {
            if (editorRef.current?.contentWindow) {
                editorRef.current.contentWindow.contentProviderUrl = replaceDomainFromEnvironmentURL(
                    process.env.REACT_APP_CRM_CONTENT_PROVIDER_URL,
                );
            }

            setTimeout(() => {
                // The Mosaico iframe shares the app origin, so stored markup is stripped of anything executable
                // before it is rendered there; the deep variant also returns a fresh copy of the saved config
                editorRef.current?.contentWindow?.initMosaico(
                    defaultHTML && !config
                        ? createMosaicoConfigsViaHTMLString(stripExecutableHtml(defaultHTML))
                        : stripExecutableHtmlDeep(config),
                    baseUrl,
                );
            });
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(initMosaico, [parsedDynamicVariables]);

    return (
        <div className={styles.container}>
            <SkeletonLoader isBusy={isLoadingPartnerSettings} height="563px">
                <iframe
                    ref={editorRef as unknown as React.RefObject<HTMLIFrameElement>}
                    style={{ width: '100%', minHeight: '500px', height: '99.9%' }}
                    title="mosaico"
                    name="crmEditorIFrame"
                    src="/mosaico/editor.html"
                />
            </SkeletonLoader>
        </div>
    );
};

export default memo(MosaicoEditor);
